import React from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import type { Toast, ToastTone } from '../hooks/useToasts';

const ICON_BY_TONE: Record<ToastTone, React.ComponentType<{ className?: string }>> = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
};

const ICON_COLOR_BY_TONE: Record<ToastTone, string> = {
  success: 'text-emerald-400',
  error: 'text-rose-400',
  info: 'text-blue-400',
};

type AppToastViewportProps = {
  toasts: Toast[];
  onDismiss: (id: string) => void;
};

/** Stapel unten mittig; auf Mobile über der Bottom-Nav und dem FAB. */
export function AppToastViewport({ toasts, onDismiss }: AppToastViewportProps) {
  if (typeof document === 'undefined' || toasts.length === 0) return null;

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 bottom-24 z-[90] flex flex-col items-center gap-2 px-4 md:bottom-6"
      role="status"
      aria-live="polite"
    >
      {toasts.map((toast) => {
        const Icon = ICON_BY_TONE[toast.tone];
        return (
          <div
            key={toast.id}
            className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-lg bg-slate-900 px-4 py-3 text-sm text-white shadow-lg"
          >
            <Icon className={`h-4 w-4 shrink-0 ${ICON_COLOR_BY_TONE[toast.tone]}`} aria-hidden />
            <span className="min-w-0 flex-1">{toast.message}</span>
            {toast.action ? (
              <button
                type="button"
                onClick={() => {
                  onDismiss(toast.id);
                  toast.action?.onClick();
                }}
                className="shrink-0 rounded px-2 py-1 text-sm font-semibold text-blue-300 hover:bg-white/10"
              >
                {toast.action.label}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => onDismiss(toast.id)}
              className="shrink-0 rounded p-1 text-slate-400 hover:bg-white/10 hover:text-white"
              aria-label="Meldung schließen"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
